/**
 * Product ID store: holds the current page of product IDs matching the global filter + date range,
 * plus total count for server-side pagination.
 * Used by ProductListCard, RecordStatsCard; similarity loads IDs via pg/search directly.
 * Clears when filter or date range changes (via store subscriptions).
 */
import { create } from 'zustand';
import { searchService } from '@/services/searchService';
import { getFilteredProductCount, getFilteredProductIdsWithCount } from '@/services/pgService';
import type { IdSet } from '@/shared/types';
import { useFullFilterStore } from './useFullFilterStore';
import { useDateRangeStore } from './useDateRangeStore';
import { getDateRangeAsDates } from '@/shared/utils';
import { toProductSearchFilterExpression } from '@truss-security/truss-sdk-internal';

/** Per-request cap aligned with pgService MAX_FILTERED_IDS_PAGE_SIZE */
const MAX_IDS_PAGE_SIZE = 1000;

let productIdFetchGeneration = 0;

type ProductSearchLikeResponse = {
  products?: any[];
  total?: number;
  totalCount?: number;
  count?: number;
};

const normalizeTotalCount = (response: ProductSearchLikeResponse, productIdsLength: number): number => {
  if (typeof response.total === 'number') return response.total;
  if (typeof response.totalCount === 'number') return response.totalCount;
  if (typeof response.count === 'number') return response.count;
  return productIdsLength;
};

export interface ProductIdStore {
  count: number;
  /** IDs for the last requested list page only (server-side pagination). */
  allProductIds: IdSet[];
  /** Last successfully fetched list page (1-based), for debugging / consistency. */
  currentPage: number;
  /**
   * True after a completed fetch for the current filter/date (including count === 0).
   * Resets when filter/date clears cached product state.
   */
  productIdsQueryDone: boolean;
  isLoading: boolean;
  error: string | null;
  fetchProductIdsPage: (page: number, pageSize: number) => Promise<void>;
  /** Total matches only (does not touch list IDs or invalidation generation). */
  fetchProductMatchCount: () => Promise<void>;
  /** Invalidate in-flight fetches and clear cached IDs (e.g. view refresh). */
  resetPagedProductIds: () => void;
}

export const useProductIdStore = create<ProductIdStore>((set) => ({
  count: 0,
  allProductIds: [],
  currentPage: 1,
  productIdsQueryDone: false,
  isLoading: false,
  error: null,

  resetPagedProductIds: () => {
    productIdFetchGeneration += 1;
    set({
      allProductIds: [],
      count: 0,
      currentPage: 1,
      error: null,
      productIdsQueryDone: false,
      isLoading: false,
    });
  },

  fetchProductMatchCount: async () => {
    try {
      const { filterAst, filterExpression } = useFullFilterStore.getState();
      const productSearchFilter = toProductSearchFilterExpression(filterAst, filterExpression);
      const dateRange = useDateRangeStore.getState().dateRange;

      if (!dateRange) {
        set({ count: 0, productIdsQueryDone: true, error: 'No date range selected' });
        return;
      }

      const actualRange = getDateRangeAsDates(dateRange);
      let totalCount = 0;

      if (!productSearchFilter) {
        totalCount = await getFilteredProductCount(actualRange.from, actualRange.to, undefined);
      } else {
        const response = await searchService.searchProducts({
          filterExpression: productSearchFilter,
          page: 1,
          limit: 1,
          startDate: actualRange.from.toISOString(),
          endDate: actualRange.to.toISOString(),
        });
        totalCount = normalizeTotalCount(response as ProductSearchLikeResponse, 0);
      }

      set({
        count: totalCount,
        productIdsQueryDone: true,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching product match count:', error);
      const raw =
        error instanceof Error ? error.message : typeof error === 'string' ? error : 'Failed to fetch product count';
      const errorMessage = raw.trim() || 'Failed to fetch product count';
      set({
        error: errorMessage,
        productIdsQueryDone: false,
      });
    }
  },

  fetchProductIdsPage: async (page: number, pageSize: number) => {
    const gen = ++productIdFetchGeneration;
    const safePage = Math.max(1, Math.floor(page));
    const cappedSize = Math.min(Math.max(1, Math.floor(pageSize)), MAX_IDS_PAGE_SIZE);

    set({ isLoading: true, error: null });

    try {
      const { filterAst, filterExpression } = useFullFilterStore.getState();
      const productSearchFilter = toProductSearchFilterExpression(filterAst, filterExpression);
      const dateRange = useDateRangeStore.getState().dateRange;

      if (!dateRange) {
        console.warn('No date range set, cannot fetch product IDs');
        if (gen !== productIdFetchGeneration) return;
        set({
          isLoading: false,
          error: 'No date range selected',
          allProductIds: [],
          count: 0,
          productIdsQueryDone: true,
        });
        return;
      }

      const actualRange = getDateRangeAsDates(dateRange);

      let productIds: IdSet[] = [];
      let totalCount = 0;

      if (!productSearchFilter) {
        const result = await getFilteredProductIdsWithCount(
          actualRange.from,
          actualRange.to,
          safePage,
          cappedSize,
          undefined
        );
        productIds = result.productIds ?? [];
        totalCount = typeof result.totalCount === 'number' ? result.totalCount : productIds.length;
      } else {
        const response = await searchService.searchProducts({
          filterExpression: productSearchFilter,
          page: safePage,
          limit: cappedSize,
          startDate: actualRange.from.toISOString(),
          endDate: actualRange.to.toISOString(),
        });

        const products = Array.isArray(response.products) ? response.products : [];
        productIds = products.map((product: any) => ({
          id: product.id,
          truss_prod_id: product.truss_prod_id ?? product.trussProdId ?? null,
          timestamp: product.timestamp ?? product.pub_date ?? product.pubDate ?? null,
        }));
        totalCount = normalizeTotalCount(response as ProductSearchLikeResponse, productIds.length);
      }

      if (!productIds || !Array.isArray(productIds)) {
        throw new Error('Invalid product IDs returned from query');
      }

      const seenIds = new Set<number>();
      const dedupedIds = productIds.filter((item) => {
        if (seenIds.has(item.id)) return false;
        seenIds.add(item.id);
        return true;
      });

      if (gen !== productIdFetchGeneration) return;

      set({
        count: totalCount,
        allProductIds: dedupedIds,
        currentPage: safePage,
        productIdsQueryDone: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching product IDs:', error);
      const raw =
        error instanceof Error ? error.message : typeof error === 'string' ? error : 'Failed to fetch product IDs';
      const errorMessage = raw.trim() || 'Failed to fetch product IDs';
      if (gen !== productIdFetchGeneration) return;
      set({
        error: errorMessage,
        isLoading: false,
        allProductIds: [],
        productIdsQueryDone: false,
      });
    }
  },
}));

useFullFilterStore.subscribe((state, prevState) => {
  const prevAst = prevState.filterAst ? JSON.stringify(prevState.filterAst) : '';
  const currentAst = state.filterAst ? JSON.stringify(state.filterAst) : '';
  const prevExpr = (prevState.filterExpression ?? '').trim();
  const currentExpr = (state.filterExpression ?? '').trim();
  if (prevAst !== currentAst || prevExpr !== currentExpr) {
    productIdFetchGeneration += 1;
    useProductIdStore.setState({
      allProductIds: [],
      count: 0,
      currentPage: 1,
      error: null,
      productIdsQueryDone: false,
    });
  }
});

useDateRangeStore.subscribe((state, prevState) => {
  if (state.dateRange && prevState.dateRange) {
    const prevKey = JSON.stringify(prevState.dateRange);
    const currentKey = JSON.stringify(state.dateRange);
    if (prevKey !== currentKey) {
      productIdFetchGeneration += 1;
      useProductIdStore.setState({
        allProductIds: [],
        count: 0,
        currentPage: 1,
        error: null,
        productIdsQueryDone: false,
      });
    }
  } else if (state.dateRange && !prevState.dateRange) {
    productIdFetchGeneration += 1;
    useProductIdStore.setState({
      allProductIds: [],
      count: 0,
      currentPage: 1,
      error: null,
      productIdsQueryDone: false,
    });
  }
});
